import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faBed, faBath, faRulerCombined, faMapMarkerAlt, faFilter } from "@fortawesome/free-solid-svg-icons";

import prop1 from "../assets/realestate/prop1.png";
import jettymayfairImg from "../assets/jettymayfair/gateway_bg.jpg";
import { Helmet } from "react-helmet-async";
import seoData from "../assets/data/seo.json";

const ALL_PROPERTIES = [
  {
    id: 1,
    image: prop1,
    title: "Modern Minimalist Villa",
    location: "Beverly Hills, CA",
    price: "$4,500,000",
    beds: 5,
    baths: 6,
    sqft: "6,200",
    type: "For Sale"
  }
];

const Properties = () => {
  const seo = seoData["properties"] || {};
  const { title, description, keywords, canonical, ogImage } = seo;

  return (
    <div className="min-h-screen bg-slate-50 font-sans pt-24">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        {canonical && <link rel="canonical" href={canonical} />}
        {ogImage && <meta property="og:image" content={ogImage} />}
      </Helmet>
      {/* Header */}
      <div className="bg-primary text-white py-32 relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10 text-center">
          <span className="text-secondary font-black tracking-[0.3em] uppercase mb-4 block text-[14px]">Portfolio</span>
          <h1 className="font-black mb-6 tracking-tighter text-[32px] md:text-[50px] lg:text-[72px]">
            Our <span className="text-secondary italic font-light">Properties</span>
          </h1>
          <p className="text-slate-300 font-light max-w-3xl mx-auto text-[14px] md:text-[16px] lg:text-[20px] tracking-widest uppercase">
            Browse our exclusive collection of luxury homes and premium real estate.
          </p>
        </div>
      </div>

      {/* Filter Section */}
      <div className="container mx-auto px-6 -mt-8 relative z-20">
        <div className="bg-white p-6 rounded-[2rem] shadow-xl border border-slate-100 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex-1 w-full bg-slate-50 px-6 py-4 rounded-full flex items-center gap-3">
            <FontAwesomeIcon icon={faSearch} className="text-primary/40" />
            <input type="text" placeholder="Search by location or keyword..." className="w-full bg-transparent border-none outline-none text-primary font-bold placeholder-primary/40" />
          </div>
          
          <div className="flex gap-4 w-full md:w-auto">
            <select className="flex-1 md:w-48 px-6 py-4 bg-slate-50 rounded-full border-none outline-none text-primary font-bold appearance-none cursor-pointer">
              <option>Any Price</option>
              <option>Under $2M</option>
              <option>$2M - $5M</option>
              <option>Over $5M</option>
            </select>
            <select className="flex-1 md:w-48 px-6 py-4 bg-slate-50 rounded-full border-none outline-none text-primary font-bold appearance-none cursor-pointer">
              <option>Property Type</option>
              <option>House</option>
              <option>Apartment</option>
              <option>Villa</option>
            </select>
          </div>
          
          <button className="w-full md:w-auto px-8 py-4 bg-secondary text-primary font-black rounded-full hover:opacity-90 transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-2">
            <FontAwesomeIcon icon={faFilter} /> Apply
          </button>
        </div>
      </div>

      {/* Properties Grid */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
            {ALL_PROPERTIES.map((property) => (
              <div key={property.id} className="group rounded-[2rem] bg-white border border-slate-100 overflow-hidden hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] transition-all duration-500 cursor-pointer">
                <div className="relative h-72 overflow-hidden">
                  <img src={property.image} alt={property.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-md px-4 py-2 rounded-full shadow-lg">
                    <span className="font-black text-primary uppercase tracking-wider text-[14px]">{property.type}</span>
                  </div>
                </div>
                
                <div className="p-8">
                  <div className="text-3xl font-black text-secondary mb-2 tracking-tighter">{property.price}</div>
                  <h3 className="font-bold text-primary mb-2 line-clamp-1 text-[24px] md:text-[30px]">{property.title}</h3>
                  <p className="text-slate-500 flex items-center gap-2 font-medium mb-6 text-[12px] md:text-[14px] lg:text-[16px]">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-secondary/70" /> {property.location}
                  </p>
                  
                  <div className="flex items-center justify-between pt-6 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-primary font-bold">
                      <FontAwesomeIcon icon={faBed} className="text-secondary" /> {property.beds} <span className="text-slate-400 font-normal text-[14px]">Beds</span>
                    </div>
                    <div className="flex items-center gap-2 text-primary font-bold">
                      <FontAwesomeIcon icon={faBath} className="text-secondary" /> {property.baths} <span className="text-slate-400 font-normal text-[14px]">Baths</span>
                    </div>
                    <div className="flex items-center gap-2 text-primary font-bold">
                      <FontAwesomeIcon icon={faRulerCombined} className="text-secondary" /> {property.sqft} <span className="text-slate-400 font-normal text-[14px]">Sqft</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Jetty Mayfair Featured Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-col lg:flex-row bg-primary rounded-[3rem] overflow-hidden shadow-2xl">
            <div className="lg:w-1/2 p-12 md:p-20 flex flex-col justify-center">
              <span className="text-secondary font-black tracking-[0.3em] uppercase mb-4 text-[12px]">Featured Project</span>
              <h2 className="text-4xl md:text-5xl lg:text-6xl text-white font-serif italic mb-6">
                Jetty Mayfair
              </h2>
              <p className="text-slate-300 font-light mb-10 leading-relaxed text-[14px] md:text-[16px]">
                Discover an amazing villa plot layout designed for happiness, comfort, and a premium lifestyle! Nestled in a serene location near Rajamahendravaram, this gated community offers world-class amenities, lush green surroundings, and the perfect blend of modern living and natural beauty.
              </p>
              <a href="/properties/jettymayfair" className="inline-flex items-center justify-center gap-2 bg-secondary text-primary px-8 py-4 rounded-full font-bold uppercase tracking-widest text-xs hover:opacity-90 transition w-max">
                Explore Jetty Mayfair
              </a>
            </div>
            <div className="lg:w-1/2 relative min-h-[400px]">
              <img src={jettymayfairImg} alt="Jetty Mayfair" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Properties;
